import { FunctionComponent, Ref, useState } from "react";

import { useTranslation } from "react-i18next";

import { faChevronDown, faChevronRight } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components/Icon/Icon";
import { Conversation } from "@/pages/Private/pages/Apollo/schema/get-people";

import { ListPerson } from "../../../schema/list";
import { isDummyProspect } from "../../../utils/columns/dummyProspect";
import { groupThreadConversations, ThreadSegment } from "../utils/groupThreadConversations";
import { useEarlierConversation } from "../utils/useEarlierConversation";

import { ConversationBubble } from "./ConversationBubble";
import { EarlierConversationBar } from "./EarlierConversationBar";

interface ThreadConversationListProps {
	conversations?: Conversation[];
	lead?: ListPerson;
	lastReceivedRef: Ref<HTMLDivElement>;
	onSaveAsTemplate: (message: string) => void;
	/** The extension renders this subtree in a shadow root; the bubbles' popups target this. */
	portalContainer?: HTMLElement | null;
}

/**
 * The lead's thread, with colleagues' own conversations folded away (PREC-3911).
 *
 * The panel loads every conversation the *prospect* has, so a colleague chatting with the
 * same person on their own LinkedIn account used to appear inline here — rendered as part of
 * this lead's outreach, down to the "(You)" byline on their messages. They stay visible,
 * because knowing a teammate is already talking to this person is the useful part, but as one
 * collapsed line the reader can open rather than a stretch of someone else's conversation.
 *
 * It is also where the viewer pulls in their own earlier conversation with the lead (PREC-4305):
 * "Load earlier conversation" on top, and the imported messages woven into the thread by date,
 * each labelled "Imported". A placeholder row has no person to look up, so it has no button.
 */
export const ThreadConversationList: FunctionComponent<ThreadConversationListProps> = ({
	conversations,
	lead,
	lastReceivedRef,
	onSaveAsTemplate,
	portalContainer,
}) => {
	const listPersonId = lead && !isDummyProspect(lead) ? lead.id : null;
	const earlier = useEarlierConversation(listPersonId, conversations);
	const thread = earlier.thread;
	const lastIndex = (thread?.length ?? 0) - 1;
	const segments = groupThreadConversations(thread);

	const renderBubble = (conversation: Conversation, isLastReceived: boolean) => (
		<ConversationBubble
			key={conversation.id}
			conversation={conversation}
			isLastReceived={isLastReceived}
			lastReceivedRef={lastReceivedRef}
			lead={lead}
			portalContainer={portalContainer}
			onSaveAsTemplate={onSaveAsTemplate}
		/>
	);

	return (
		<>
			{listPersonId && (
				<EarlierConversationBar
					isBusy={earlier.isBusy}
					isLoading={earlier.isLoading}
					requestFailed={earlier.requestFailed}
					resultLines={earlier.resultLines}
					shared={earlier.shared}
					truncated={earlier.truncated}
					onLoad={earlier.load}
				/>
			)}
			{segments.map((segment: ThreadSegment) =>
				segment.kind === "own" ? (
					renderBubble(segment.conversation, segment.index === lastIndex)
				) : (
					<OtherAccountThread
						key={`other-${segment.key}-${segment.conversations[0]?.id}`}
						name={segment.name}
						renderBubble={renderBubble}
						segment={segment}
					/>
				)
			)}
		</>
	);
};

interface OtherAccountThreadProps {
	name: string;
	segment: Extract<ThreadSegment, { kind: "other" }>;
	renderBubble: (conversation: Conversation, isLastReceived: boolean) => JSX.Element;
}

/** One collapsed run of a colleague's messages, expandable in place. */
const OtherAccountThread: FunctionComponent<OtherAccountThreadProps> = ({
	name,
	segment,
	renderBubble,
}) => {
	const { t } = useTranslation();
	const [isExpanded, setIsExpanded] = useState(false);
	const count = segment.conversations.length;

	return (
		<div className="w-full my-2">
			<button
				className="w-full flex cursor-pointer items-center gap-2 px-3 py-2 rounded-lg border border-dashed border-gray-200 text-sm text-text-meta hover:border-brand-light duration-200"
				type="button"
				onClick={() => setIsExpanded((expanded) => !expanded)}
			>
				<Icon className="w-3 h-3" icon={isExpanded ? faChevronDown : faChevronRight} />
				<span>{t("inbox.thread.otherAccountConversation", { count, name })}</span>
			</button>
			{isExpanded && (
				<div className="mt-2 pl-3 border-l-2 border-gray-200 flex flex-col gap-2">
					{segment.conversations.map((conversation) => renderBubble(conversation, false))}
				</div>
			)}
		</div>
	);
};
